import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Inventory, InventoryMutation, Item, Make, Model } from '@wb/core/interfaces/inventory';
import {
  InventoryDeleteGQL,
  InventoryInsertGQL,
  InventoryPriceResetGQL,
  InventoryResetMarketScanIdGQL,
  InventorySearchMarketScanIdGQL,
  InventoryUpdateGQL,
  InventoryUpdateListingGQL,
} from '@wb/core/schema/mutation-definitions/inventory.graphql';
import {
  InventoriesFilter,
  InventoriesGQL,
  InventoriesResponse,
  InventoryFilterData,
  InventoryFilterDataGQL,
  InventoryQueryVariables,
  MakesGQL,
  MakesResponse,
} from '@wb/core/schema/query-definitions/inventory.graphql';
import { AccountService } from '@wb/core/services/account.service';
import { QueryRef } from 'apollo-angular';
import { BehaviorSubject, Observable, ReplaySubject, Subject, firstValueFrom } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class InventoryService implements OnDestroy {
  private inventoriesQueryRef: QueryRef<InventoriesResponse, InventoryQueryVariables> | undefined;

  private makesQueryRef: QueryRef<MakesResponse> | undefined;

  private destroyed$: Subject<void> = new Subject<void>();

  private inventories$: ReplaySubject<Inventory[]> = new ReplaySubject<Inventory[]>(1);

  private inventoryCount$: ReplaySubject<number> = new ReplaySubject<number>(0);

  private makes$: ReplaySubject<Make[]> = new ReplaySubject<Make[]>(1);

  private models$: ReplaySubject<Model[]> = new ReplaySubject<Model[]>(1);

  private inventoryFilterData$: ReplaySubject<InventoryFilterData> = new ReplaySubject<InventoryFilterData>(1);

  private selectedInventory$: BehaviorSubject<Inventory | undefined> = new BehaviorSubject<Inventory | undefined>(
    undefined,
  );

  private selectedInventoryItem$: BehaviorSubject<Item | undefined> = new BehaviorSubject<Item | undefined>(undefined);

  constructor(
    private readonly inventoriesGQL: InventoriesGQL,
    private readonly inventoryFilterDataGQL: InventoryFilterDataGQL,
    private readonly inventoryInsertGQL: InventoryInsertGQL,
    private readonly inventoryUpdateGQL: InventoryUpdateGQL,
    private readonly inventoryPriceResetGQL: InventoryPriceResetGQL,
    private readonly inventorySearchMarketScanIdGQL: InventorySearchMarketScanIdGQL,
    private readonly inventoryResetMarketScanIdGQL: InventoryResetMarketScanIdGQL,
    private readonly inventoryUpdateListingGQL: InventoryUpdateListingGQL,
    private readonly inventoryDeleteGQL: InventoryDeleteGQL,
    private readonly makesGQL: MakesGQL,
    private readonly account: AccountService,
  ) {
    this.account.selectedAccount.pipe(takeUntil(this.destroyed$)).subscribe((selectedAccount) => {
      if (selectedAccount) {
        this.fetchInventory({ where: { account_uid: selectedAccount?.uid } });
        this.fetchMakes();
        this.fetchInventoryFilterData(selectedAccount?.uid);
      }
    });
  }

  refetchInventory(input: InventoriesFilter = {}, count: number = 10, offset: number = 0): void {
    if (input?.where?.account_uid === undefined) {
      const accountUid = localStorage.getItem('accountUid');
      if (accountUid) {
        if (!input.where) {
          input.where = {};
        }

        input.where.account_uid = accountUid;
      }
    }

    const accountUid = localStorage.getItem('accountUid');
    if (accountUid) {
      this.fetchInventory(input, count, offset);
    }
  }

  refetchInventoryFilterData(): void {
    const accountUid = localStorage.getItem('accountUid');
    if (accountUid) {
      this.fetchInventoryFilterData(accountUid);
    }
  }

  refetchAndSelectSingleInventory(uid: string, acctUid: string): void {
    const accountUid = localStorage.getItem('accountUid') || acctUid;
    if (accountUid) {
      this.inventoriesQueryRef = this.inventoriesGQL.watch(
        {
          input: {
            where: {
              account_uid: accountUid,
              uid,
            },
          },
          limit: 1,
        },
        { fetchPolicy: 'no-cache' },
      );
      this.inventoriesQueryRef.valueChanges
        .pipe(
          map((result) => result.data),
          takeUntil(this.destroyed$),
        )
        .subscribe({
          next: (response: InventoriesResponse) => {
            this.selectedInventory$.next(
              response.inventories.success ? response?.inventories?.data?.data[0] : ({} as Inventory),
            );
          },
          error: (err: HttpErrorResponse) => {
            this.selectedInventory$.next({} as Inventory);
            if (err) {
              console.error(err);
            }
          },
        });
    }
  }

  public fetchInventory(input: InventoriesFilter, limit: number = 10, offset: number = 0) {
    this.inventoriesQueryRef = this.inventoriesGQL.watch({ input, limit, offset }, { fetchPolicy: 'no-cache' });

    this.inventoriesQueryRef.valueChanges
      .pipe(
        map((result) => result.data),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (response: InventoriesResponse) => {
          this.inventories$.next(response.inventories.success ? response.inventories?.data?.data : []);
          this.inventoryCount$.next(response.inventories.success ? response.inventories?.data?.count : 0);
        },
        error: (err: HttpErrorResponse) => {
          this.inventories$.next([]);
          if (err) {
            console.error(err);
          }
        },
      });
  }

  public fetchInventoryFilterData(accountUid: string): void {
    this.inventoryFilterDataGQL
      .fetch({ accountUid })
      .pipe(
        map((result) => result.data),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (response) => {
          if (response.inventoryFilterData.success) {
            this.inventoryFilterData$.next(response.inventoryFilterData.filterData);
          }
        },
        error: (err: HttpErrorResponse) => {
          if (err) {
            console.error(err);
          }
        },
      });
  }

  public fetchMakes() {
    this.makesQueryRef = this.makesGQL.watch({ limit: 0 }, { fetchPolicy: 'no-cache' });

    this.makesQueryRef?.valueChanges
      .pipe(
        map((result) => result.data),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (response: MakesResponse) => {
          this.makes$.next(response.makes.success ? response.makes.makes : []);
        },
        error: (err: HttpErrorResponse) => {
          this.makes$.next([]);
          if (err) {
            console.error(err);
          }
        },
      });
  }

  get inventories(): Observable<Inventory[]> {
    return this.inventories$.asObservable();
  }

  get inventoryCount(): Observable<number> {
    return this.inventoryCount$.asObservable();
  }

  get inventoryFilterData(): Observable<InventoryFilterData> {
    return this.inventoryFilterData$.asObservable();
  }

  // Mapping inventory to an item object in order to reduce data and remove unused properties.
  get inventoryItems(): Observable<Item[]> {
    return this.inventories$.pipe(
      map((results: Inventory[]) =>
        results.map((result: Inventory) => {
          return {
            uid: result.uid,
            stockId: result.stock_id,
            type: result.type,
            vin: result.vin,
            year: result.year,
            make: result.make,
            model: result.model,
            options: result.options,
            body: result.body,
            trim: result.trim,
            doors: result.doors,
            image: result.image,
            history: result.history,
            priceMsrp: result.price_msrp,
            priceAdvert: result.price_advert,
            priceWebbuy: result.price_web,
            priceInvoice: result.price_invoice,
            priceVariable: result.price_variable,
            priceAfterIncentives: result.price_after_incentives,
            priceWholesale: result.price_wholesale,
            mpg: result.mpg,
            mileage: result.mileage,
            exteriorColor: result.exterior_color,
            exteriorColorBase: result.exterior_color_base,
            interiorColor: result.interior_color,
            interiorColorBase: result.interior_color_base,
            drivetrain: result.drivetrain,
            transmission: result.transmission,
            fuelType: result.fuel_type,
            engineCylinders: result.engine_cylinders,
            engineDisplacement: result.engine_displacement,
            certified: result.certified,
            dateInStock: result.date_in_stock,
            imageHash: result.image_hash,
            imageSource: result.image_source,
            stockImage: result.stock_image,
            grossWeight: result.gross_weight,
            optionCodes: result.option_codes,
            delisted: result.delisted,
            noPrice: result.noPrice,
            dataone: result.dataone,
            createdAt: result.created_at,
            updatedAt: result.updated_at,
          } as Item;
        }),
      ),
    );
  }

  get selectedInventory(): Observable<Inventory | undefined> {
    return this.selectedInventory$.asObservable();
  }

  get selectedInventoryItem(): Observable<Item | undefined> {
    return this.selectedInventoryItem$.asObservable();
  }

  get makes(): Observable<Make[]> {
    return this.makes$.asObservable();
  }

  get models(): Observable<Model[]> {
    return this.models$.asObservable();
  }

  async selectInventory(inventoryUid: string): Promise<void> {
    const inventories: Inventory[] = await firstValueFrom(this.inventories);
    this.selectedInventory$.next(inventories.find((inventory: Inventory) => inventory.uid === inventoryUid));
  }

  async selectInventoryItem(inventoryUid: string): Promise<void> {
    const inventoryItems: Item[] = await firstValueFrom(this.inventoryItems);
    this.selectedInventoryItem$.next(inventoryItems.find((inventoryItem: Item) => inventoryItem.uid === inventoryUid));
  }

  async insertInventoryItem(accountUid: string, item: Item, callback?: Function): Promise<void> {
    this.inventoryInsertGQL
      .mutate({ accountUid: accountUid.toString(), input: this.itemToInventory(item) }, { fetchPolicy: 'no-cache' })
      .pipe(
        map((result) => result),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (response) => {
          if (callback && response.data) {
            callback(response.data.insertInventory.inventory.uid);
          }
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
        },
      });
  }

  async updateInventoryItem(inventoryItem: Item, callback?: Function): Promise<void> {
    this.inventoryUpdateGQL
      .mutate({ uid: inventoryItem.uid, input: this.itemToInventory(inventoryItem) }, { fetchPolicy: 'no-cache' })
      .pipe(
        map((result) => result.data),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: () => {
          if (inventoryItem?.uid) {
            this.selectInventoryItem(inventoryItem.uid);
          }
          if (callback) {
            callback();
          }
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
        },
      });
  }

  async changeInventoryListing(uid: string, delisted: boolean, callback?: Function): Promise<void> {
    this.inventoryUpdateListingGQL
      .mutate({ uid: uid, delisted: delisted }, { fetchPolicy: 'network-only' })
      .pipe(
        map((result) => result),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: () => {
          if (callback) {
            callback();
          }
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
        },
      });
  }

  async deleteInventoryItem(uid: string, callback?: Function): Promise<void> {
    this.inventoryDeleteGQL
      .mutate({ uid: uid }, { fetchPolicy: 'network-only' })
      .pipe(
        map((result) => result.data),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: () => {
          if (callback) {
            callback();
          }
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
        },
      });
  }

  private itemToInventory(item: Item): InventoryMutation {
    return {
      stock_id: item.stockId,
      type: item.type,
      vin: item.vin,
      year: item.year,
      make: item.make,
      model: item.model,
      options: item.options,
      body: item.body,
      bodyStyle: item.bodyStyle,
      trim: item.trim,
      doors: item.doors,
      image: item.image,
      history: item.history,
      price_msrp: item.priceMsrp ? item.priceMsrp : 0,
      price_advert: item.priceAdvert ? item.priceAdvert : 0,
      price_web: item.priceWebbuy ? item.priceWebbuy : 0,
      price_invoice: item.priceInvoice ? item.priceInvoice : 0,
      price_variable: item.priceVariable ? item.priceVariable : 0,
      price_after_incentives: item.priceAfterIncentives ? item.priceAfterIncentives : 0,
      price_wholesale: item.priceWholesale ? item.priceWholesale : 0,
      mpg: item.mpg,
      mileage: item.mileage ? item.mileage : 0,
      exterior_color: item.exteriorColor,
      exterior_color_base: item.exteriorColorBase,
      interior_color: item.interiorColor,
      interior_color_base: item.interiorColorBase,
      drivetrain: item.drivetrain,
      transmission: item.transmission,
      fuel_type: item.fuelType,
      engine_cylinders: item.engineCylinders,
      engine_displacement: item.engineDisplacement,
      certified: item.certified ? item.certified : 0,
      date_in_stock: item.dateInStock,
      image_hash: item.imageHash,
      image_source: item.imageSource,
      gross_weight: item.grossWeight,
      option_codes: item.optionCodes,
      delisted: item.delisted,
      created_at: item.createdAt,
      updated_at: item.updatedAt,
      shared_inventory: 0,
      jato_vehicle_id: '',
      jato_vehicle_id_date: new Date().toUTCString(),
      dealer_id: '',
    };
  }

  public static parseCurrency(currency: any): number {
    if (typeof currency === 'string') {
      let num: string | undefined = currency.match(/[\d\.]/g)?.join(''); // Only take numbers

      let parsed = num && num !== '0' ? Number.parseFloat(num) : -1;

      if (parsed < 0) {
        parsed = 0;
      }

      return parsed;
    } else if (typeof currency === 'number') {
      return currency;
    }

    return 0;
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public priceReset(accountUid: string, userUid: string, priceList: any): Promise<any> {
    return this.inventoryPriceResetGQL
      .mutate({ accountUid, userUid, input: priceList }, { fetchPolicy: 'no-cache' })
      .pipe(
        map((result) => result.data),
        takeUntil(this.destroyed$),
      )
      .toPromise();
  }

  public searchMarketScanId(inventoryUid: string): Promise<any> {
    return this.inventorySearchMarketScanIdGQL
      .fetch({ uid: inventoryUid }, { fetchPolicy: 'no-cache' })
      .pipe(
        map((result) => result.data),
        takeUntil(this.destroyed$),
      )
      .toPromise();
  }

  public resetMarketScanId(inventoryUid: string, marketScanId: number): Promise<any> {
    return this.inventoryResetMarketScanIdGQL
      .mutate({ uid: inventoryUid, marketScanId: marketScanId }, { fetchPolicy: 'no-cache' })
      .pipe(
        map((result) => result.data),
        takeUntil(this.destroyed$),
      )
      .toPromise();
  }
}
